export const ONRAMP_RETURN_MESSAGE_TYPE = 'deepsage:onramp-return';

/** Alchemy / MixPay 支付完成回跳页（iframe 或收银标签页加载并向父窗口 postMessage） */
export function resolveOnrampReturnUrl() {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/deposit/onramp-return`;
}

export function isOnrampReturnMessage(data) {
  if (!data || typeof data !== 'object') return false;
  return data.type === ONRAMP_RETURN_MESSAGE_TYPE;
}

export function postOnrampReturnToHost(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return;
  const payload = { type: ONRAMP_RETURN_MESSAGE_TYPE };
  const targetOrigin = win.location.origin;
  try {
    if (win.parent && win.parent !== win) {
      win.parent.postMessage(payload, targetOrigin);
    }
    if (win.opener) {
      win.opener.postMessage(payload, targetOrigin);
    }
  } catch (err) {
    console.warn('[onramp-return] postMessage failed', err);
  }
}

/**
 * @param {Window} [win]
 * @returns {'iframe'|'popup'|'top'}
 */
export function resolveOnrampReturnHostKind(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return 'top';
  try {
    if (win.parent && win.parent !== win) return 'iframe';
    if (win.opener) return 'popup';
  } catch {
    // cross-origin parent → treat as embedded
    return 'iframe';
  }
  return 'top';
}

export function tryCloseOnrampReturnWindow(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return false;
  try {
    win.close();
  } catch {
    // browsers may throw if the tab was not script-opened
  }
  try {
    return Boolean(win.closed);
  } catch {
    return false;
  }
}

/**
 * Bridge page finish: notify host → close popup → else redirect account.
 * Never leave an infinite spinner on MixPay/Alchemy return tabs.
 *
 * @param {{
 *   win?: Window|null,
 *   postMessage?: (win?: Window|null) => void,
 *   resolveKind?: (win?: Window|null) => 'iframe'|'popup'|'top',
 *   tryClose?: (win?: Window|null) => boolean,
 *   focusOpener?: (win?: Window|null) => void,
 *   onRedirect: () => void,
 *   notifyDelayMs?: number,
 *   closeCheckMs?: number,
 *   iframeFallbackMs?: number,
 *   setTimeoutFn?: typeof setTimeout,
 * }} opts
 * @returns {{ mode: string, timers: number[] }}
 */
export function scheduleOnrampReturnFinish(opts) {
  const {
    win = typeof window !== 'undefined' ? window : null,
    postMessage = postOnrampReturnToHost,
    resolveKind = resolveOnrampReturnHostKind,
    tryClose = tryCloseOnrampReturnWindow,
    focusOpener = (w) => {
      try {
        if (w?.opener && !w.opener.closed) w.opener.focus();
      } catch {
        // ignore
      }
    },
    onRedirect,
    notifyDelayMs = 200,
    closeCheckMs = 350,
    iframeFallbackMs = 2000,
    setTimeoutFn = setTimeout,
  } = opts || {};

  if (typeof onRedirect !== 'function') {
    throw new Error('scheduleOnrampReturnFinish requires onRedirect');
  }

  const timers = [];
  postMessage(win);
  const kind = resolveKind(win);

  if (kind === 'top') {
    timers.push(
      setTimeoutFn(() => {
        onRedirect();
      }, notifyDelayMs)
    );
    return { mode: 'redirect-top', timers };
  }

  if (kind === 'iframe') {
    // Parent DepositDialog tears down iframe on postMessage; if still alive, escape to account.
    timers.push(
      setTimeoutFn(() => {
        onRedirect();
      }, iframeFallbackMs)
    );
    return { mode: 'iframe-fallback-redirect', timers };
  }

  // popup / named tab (MixPay): close after host can process message; else Account.
  timers.push(
    setTimeoutFn(() => {
      focusOpener(win);
      if (tryClose(win)) return;
      timers.push(
        setTimeoutFn(() => {
          try {
            if (win?.closed) return;
          } catch {
            // ignore
          }
          onRedirect();
        }, closeCheckMs)
      );
    }, notifyDelayMs)
  );
  return { mode: 'close-then-redirect', timers };
}
